import { useState } from 'react';
import { PDFDocument, type PDFAcroField } from 'pdf-lib';
import { useQuery } from '@tanstack/react-query';
import {
  DocumentTypes,
  useAddOrUpdateDocument,
  useGetUploadedFileLink,
  type UploadedDocument,
} from '@urbanx/agx.campaigns.client';
import { usePreparePdfForEditing } from '@urbanx/agx.pdf.client';
import {
  AgxPdfEditor,
  AgxButton,
  AgxLoadedFileDropzone,
  AgxLoadingState,
  AgxModal,
  AgxModalTitleIcon,
  AgxSpacer,
  AgxTypography,
  Breakpoints,
  Icon_AlertCircleOutline,
  generateEditedPdf,
  importPdfFieldBlocks,
  importRepositionableFields,
  transformPdfAnchor,
  usePdfEditorDocument,
  useWindowSize,
  type AgxDialogControlProps,
  type BrowserLoadedFile,
  type PdfEditorState,
} from '@urbanx/agx.ui-components2';
import * as contractPdfEditorStyles from 'components/ContractPdfEditorModal.css';
import { useAppDispatch } from 'hooks/useAppDispatch';
import { useAppSelector } from 'hooks/useAppSelector';
import { cleanStreetAddress } from 'utils/formatAddress';
import { setAndShowErrorToast } from 'store/config';
import {
  contractReplacementFieldBlockTemplates,
  getContractReplacementPdfConfiguration,
} from './contractReplacementPdfTemplate';
import * as styles from './ContractReplacementUpload.css';

interface ReplaceContractDialogProps extends AgxDialogControlProps {
  campaignId: string;
  contractId: string;
  hasExistingContract?: boolean;
  uploadedDocument?: UploadedDocument;
  onComplete: (document: UploadedDocument) => void;
}

const ReplaceContractDialog = ({
  campaignId,
  contractId,
  hasExistingContract = true,
  uploadedDocument,
  onComplete,
  isOpen,
  setIsDialogOpen,
}: ReplaceContractDialogProps) => {
  const dispatch = useAppDispatch();
  const isMobile = useWindowSize() === Breakpoints.Mobile;
  const address = useAppSelector((state) => state.form.address);
  const propertyAddress = address ? cleanStreetAddress(address) : null;
  const { addOrUpdateDocument } = useAddOrUpdateDocument();
  const { preparePdfForEditing } = usePreparePdfForEditing();
  const { data: fileLink, error: fileLinkError } = useGetUploadedFileLink(
    uploadedDocument?.containerFilePath ?? '',
    !!uploadedDocument?.containerFilePath && !uploadedDocument.publicPath
  );
  const document = usePdfEditorDocument({
    uploadedDocument,
    enabled: !!(uploadedDocument?.publicPath || fileLink),
    getFileLink: async () => fileLink,
  });
  const imported = useQuery({
    queryKey: [
      'replacement-contract-field-blocks',
      document.documentKey,
      uploadedDocument?.containerFilePath,
      uploadedDocument?.publicPath,
    ],
    enabled: !!document.pdfBytes,
    staleTime: Infinity,
    gcTime: 0,
    retry: false,
    queryFn: async ({ signal }) => {
      if (!document.pdfBytes) throw new Error('The replacement contract is not loaded.');
      const bytes = new Uint8Array(await document.pdfBytes.arrayBuffer());
      const { fieldBlockTemplates } = getContractReplacementPdfConfiguration(
        (await PDFDocument.load(bytes)).getPageCount()
      );
      return {
        ...(await importPdfFieldBlocks(bytes, fieldBlockTemplates, signal)),
        fieldBlockTemplates,
      };
    },
  });
  const [confirmed, setConfirmed] = useState(!!uploadedDocument || !hasExistingContract);
  const [loadedFile, setLoadedFile] = useState<
    BrowserLoadedFile & ReturnType<typeof getContractReplacementPdfConfiguration>
  >();
  const [busy, setBusy] = useState(false);
  const [editState, setEditState] = useState<PdfEditorState>();
  const [editorError, setEditorError] = useState(false);
  const pdfBytes = uploadedDocument ? imported.data?.pdfBytes : loadedFile?.bytes;
  const preparedDocument = uploadedDocument ? imported.data : loadedFile;
  const initialState = preparedDocument?.editState;
  const fieldBlockTemplates =
    preparedDocument?.fieldBlockTemplates ?? contractReplacementFieldBlockTemplates;
  const initialContractDate = initialState?.fieldBlocks.find(
    (block) => block.templateId === contractReplacementFieldBlockTemplates[0].id
  );
  const loadError = fileLinkError ?? document.error ?? imported.error;
  const fileName = uploadedDocument?.fileName ?? loadedFile?.fileName ?? 'contract.pdf';

  const loadFile = async (file: BrowserLoadedFile) => {
    setBusy(true);
    setEditorError(false);
    try {
      const preparedPdf = await preparePdfForEditing(
        new Blob([file.bytes.slice().buffer], { type: 'application/pdf' })
      );
      const bytes = new Uint8Array(await preparedPdf.arrayBuffer());
      const source = await PDFDocument.load(bytes);
      const configuration = getContractReplacementPdfConfiguration(source.getPageCount());
      const definitions = configuration.fieldBlockTemplates.flatMap((template) =>
        template.fields.map((field) => ({
          name: field.exportName!,
          anchorText: field.docusignAnchorText ?? field.positionAnchorText,
        }))
      );
      const { mappingFields = [] } = await importRepositionableFields(bytes, definitions);
      // A solicitor may return an already mapped staff contract. Replace its signing
      // markers, including repeated occurrences, before adding the agent's mappings.
      for (const field of mappingFields.sort(
        (left, right) => (right.source?.anchorIndex ?? 0) - (left.source?.anchorIndex ?? 0)
      )) {
        if (field.source?.anchorText && field.source.textAdvance != null) {
          transformPdfAnchor(
            source,
            source.getPage(field.pageIndex),
            field.source.anchorText,
            field.source.anchorIndex ?? 0,
            // Preserve the text cursor so adjacent solicitor text stays in place.
            () =>
              `0 Tc [${-field.source!.textAdvance!}] TJ ${field.source!.characterSpacing ?? 0} Tc`,
            'textOperation'
          );
        }
      }
      const sourceForm = source.getForm();
      for (const field of sourceForm.getFields()) {
        if (definitions.some((definition) => definition.name === field.getName())) {
          sourceForm.removeField(field);
        } else if (field.getName().startsWith('pdfEditor.')) {
          // Source annotations are completed contract text, not this session's edits.
          // Rename their root so export keeps their values and original appearances.
          let root: PDFAcroField = field.acroField;
          while (root.getParent()) root = root.getParent()!;
          root.setPartialName(`uploadedPdfEditor_${crypto.randomUUID()}`);
        }
      }
      const cleanBytes = await source.save({ updateFieldAppearances: false });
      setLoadedFile({
        ...file,
        bytes: cleanBytes,
        arrayBuffer: cleanBytes.slice().buffer,
        ...configuration,
      });
      setEditState(undefined);
    } catch {
      dispatch(setAndShowErrorToast('Unable to prepare the contract for editing.'));
    } finally {
      setBusy(false);
    }
  };

  const complete = async () => {
    if (!pdfBytes || !initialState || busy || editorError || loadError) return;
    setBusy(true);
    try {
      const bytes = await generateEditedPdf({
        pdfBytes,
        editState: editState ?? initialState,
        fieldBlockTemplates,
        preserveFieldBlocks: true,
      });
      const file = new File([bytes.slice().buffer], fileName, { type: 'application/pdf' });
      const saved = await addOrUpdateDocument({
        campaignId,
        contractId,
        documentType: DocumentTypes.BlankSalesContract,
        filePath: `${campaignId}/contracts/${contractId}/uploads/${crypto.randomUUID()}/${file.name}`,
        body: { file },
      });
      if (!saved) throw new Error('The replacement contract was not uploaded.');
      onComplete(saved);
      setIsDialogOpen(false);
    } catch {
      dispatch(setAndShowErrorToast('Unable to save the replacement contract.'));
    } finally {
      setBusy(false);
    }
  };

  let title = 'Replacing Contract';
  if (confirmed) {
    title = uploadedDocument ? 'Edit Contract' : 'Contract Upload';
    if (hasExistingContract) {
      title = uploadedDocument ? 'Edit replacement contract' : 'Replacement Contract Upload';
    }
    if (isMobile) {
      title = pdfBytes
        ? `Editing Contract${propertyAddress ? ` for ${propertyAddress}` : ''}`
        : hasExistingContract
          ? 'Replacement Contract Upload'
          : 'Contract Upload';
    }
  }

  return (
    <AgxModal
      id="replace-contract-modal"
      className={contractPdfEditorStyles.dialog}
      headerClassName={confirmed ? contractPdfEditorStyles.dialogHeader : styles.confirmationHeader}
      actionButtonsClassName={styles.dialogActions}
      dividerClassName={contractPdfEditorStyles.dialogDivider}
      isOpen={isOpen}
      title={title}
      titleIcon={
        !confirmed ? (
          <AgxModalTitleIcon tone="warning">
            <Icon_AlertCircleOutline type="Warning" height={40} width={40} />
          </AgxModalTitleIcon>
        ) : undefined
      }
      titleIconPosition={isMobile ? 'above' : 'inline'}
      type={confirmed ? 'fullHeight' : 'single'}
      minWidth={confirmed ? '95vw' : '600px'}
      maxWidth={confirmed ? '95vw' : '600px'}
      variant={confirmed ? 'withDivider' : 'withoutDivider'}
      actionButtonContainerStyle="withDivider"
      setIsDialogOpen={(open) => {
        if (!busy) setIsDialogOpen(open);
      }}
      content={
        !confirmed ? (
          <AgxSpacer padding={{ left: 'xl', right: 'xl', bottom: 'spacing-3xl' }}>
            <AgxTypography type="body_ui" size="small">
              You must upload a Contract with the Buyer and offer information completed. You can
              revert back to the Blank Contract by deleting this replacement Contract.
            </AgxTypography>
          </AgxSpacer>
        ) : (
          <div className={styles.editor} inert={busy}>
            {loadError ? (
              <AgxTypography>Unable to load the replacement contract.</AgxTypography>
            ) : pdfBytes && initialState ? (
              <AgxPdfEditor
                id="replacement-contract-editor"
                pdfBytes={pdfBytes}
                pdfFileName={fileName}
                documentKey={uploadedDocument ? document.documentKey : loadedFile?.id}
                initialEditState={initialState}
                fieldBlockTemplates={fieldBlockTemplates}
                initialPageIndex={initialContractDate?.pageIndex ?? 1}
                initialPageAlignment="start"
                initialPageOffsetYPct={
                  initialContractDate && Math.max(0, initialContractDate.yPct - 0.05)
                }
                toolsPosition="left"
                compact={isMobile}
                showNoEditingNotice={isMobile}
                showToolbar={!isMobile}
                showTools={!isMobile}
                showFieldBlockPagePicker
                showAddText={false}
                showStrikeOut={false}
                onChange={setEditState}
                onError={() => setEditorError(true)}
              />
            ) : uploadedDocument || busy ? (
              <AgxLoadingState className={styles.loading} />
            ) : (
              <div className={styles.upload}>
                <AgxLoadedFileDropzone
                  id="replacement-contract-file"
                  dataTestId="replacement-contract-dropzone"
                  accept=".pdf"
                  extensions={['pdf']}
                  multiple={false}
                  loadedFile={loadedFile}
                  onFileLoaded={loadFile}
                  onLoadError={() => dispatch(setAndShowErrorToast('Unable to read the PDF.'))}
                  fillVertical
                  compact={isMobile}
                />
              </div>
            )}
          </div>
        )
      }
      actionButtons={
        <AgxSpacer direction="horizontal" gap="spacing-md" contentJustification="center" fill>
          {!confirmed ? (
            <>
              <AgxButton
                id="cancel-replace-contract"
                text="Cancel"
                variant="hollow"
                size="large"
                onClick={() => setIsDialogOpen(false)}
                stretch
              />
              <AgxButton
                id="continue-replace-contract"
                text="Continue"
                variant="primary"
                size="large"
                onClick={() => setConfirmed(true)}
                stretch
              />
            </>
          ) : (
            <AgxButton
              id="confirm-replacement-fields"
              text={
                busy
                  ? pdfBytes
                    ? 'Saving...'
                    : 'Preparing preview...'
                  : 'Contract date & signature fields mapped correctly'
              }
              variant="primary"
              size="large"
              disabled={!pdfBytes || busy || editorError || !!loadError}
              onClick={complete}
              stretch={isMobile}
            />
          )}
        </AgxSpacer>
      }
    />
  );
};

export default ReplaceContractDialog;
